// engine-exception: the one-click rating page is opened from an email footer by a customer with no account — no shell, no engine client auth; a signed-token capability page like the public quote
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApiError, type Client } from '../../engine/api/client';
import { publicClient } from '../booking/BookingPage';

// "How did we do?" (docs/servicedesk-v2-proposal.md P12). The rating IS the link: the score is in
// the URL the customer clicked, and this page registers it on arrival, then offers the optional
// comment. That is the whole difference between a 3% survey response rate and a 30% one.
//
// The registration is a POST from here rather than a GET the mail client could make, because
// mail clients and security scanners fetch links in the background — a GET that scored would
// invent ratings nobody gave.

interface Rated { ticket_ref: string | null; score: number; already: boolean }
interface Opened { ticket_ref: string | null; summary: string | null; score: number | null }

const LABELS = ['Poor', 'Fair', 'OK', 'Good', 'Excellent'];

export function RatePage({ client }: { client?: Client } = {}) {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const api = useMemo(() => client ?? publicClient(), [client]);
  const clicked = Number(params.get('score'));
  const sent = useRef(false);

  const [state, setState] = useState<{ phase: 'loading' | 'rated' | 'choose' | 'error'; error?: string; rated?: Rated; opened?: Opened }>({ phase: 'loading' });
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    const go = async () => {
      try {
        const opened = await api.request<Opened>(`/public/csat/${encodeURIComponent(token)}`);
        if (!Number.isInteger(clicked) || clicked < 1 || clicked > 5) {
          setState({ phase: 'choose', opened });
          return;
        }
        const rated = await api.request<Rated>(`/public/csat/${encodeURIComponent(token)}`, { method: 'POST', body: { score: clicked } });
        setState({ phase: 'rated', rated, opened });
      } catch (e) {
        const msg = e instanceof ApiError ? (e.detail ?? e.message) : e instanceof Error ? e.message : 'This link is not valid';
        setState({ phase: 'error', error: msg });
      }
    };
    void go();
  }, [api, token, clicked]);

  const choose = async (score: number) => {
    try {
      const rated = await api.request<Rated>(`/public/csat/${encodeURIComponent(token)}`, { method: 'POST', body: { score } });
      setState((s) => ({ ...s, phase: 'rated', rated }));
    } catch (e) {
      setState({ phase: 'error', error: e instanceof ApiError ? (e.detail ?? e.message) : 'Something went wrong' });
    }
  };

  const addComment = async () => {
    const text = comment.trim();
    if (!text || !state.rated) return;
    await api.request(`/public/csat/${encodeURIComponent(token)}`, { method: 'POST', body: { score: state.rated.score, comment: text } });
    setSaved(true);
  };

  if (state.phase === 'loading') return <main style={wrap}><p>One moment…</p></main>;
  if (state.phase === 'error') return <main style={wrap}><h1 style={h1}>That link has expired</h1><p>{state.error}</p></main>;

  if (state.phase === 'choose') {
    return (
      <main style={wrap}>
        <h1 style={h1}>How did we do?</h1>
        {state.opened?.summary && <p style={muted}>{state.opened.ticket_ref} — {state.opened.summary}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          {LABELS.map((label, i) => (
            <button key={label} type="button" style={button} onClick={() => void choose(i + 1)}>{i + 1} {label}</button>
          ))}
        </div>
      </main>
    );
  }

  return (
    <main style={wrap}>
      <h1 style={h1}>{state.rated?.already ? 'You already rated this' : 'Thank you'}</h1>
      <p style={muted}>
        {state.rated?.ticket_ref ? `${state.rated.ticket_ref} — ` : ''}
        you gave us {state.rated?.score} out of 5.
      </p>
      {saved ? (
        <p>Thanks — we have passed that on.</p>
      ) : (
        <>
          <label htmlFor="csat-comment" style={{ display: 'block', marginTop: 20 }}>Anything you would like to add? (optional)</label>
          <textarea
            id="csat-comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid #d7d2c7', borderRadius: 8, font: 'inherit' }}
          />
          <button type="button" style={{ ...button, marginTop: 10 }} onClick={() => void addComment()} disabled={!comment.trim()}>Send</button>
        </>
      )}
    </main>
  );
}

const wrap: React.CSSProperties = { maxWidth: 560, margin: '0 auto', padding: '48px 20px', fontFamily: 'system-ui, sans-serif', color: '#1b1915', lineHeight: 1.5 };
const h1: React.CSSProperties = { fontSize: 24, margin: '0 0 4px' };
const muted: React.CSSProperties = { color: '#5c574f', marginTop: 0 };
const button: React.CSSProperties = { padding: '10px 14px', border: '1px solid #d7d2c7', borderRadius: 8, background: '#fff', font: 'inherit', cursor: 'pointer' };
